import { takesMoney } from "../utils/takesMoney";
import { openMoneyGate } from "../lib/connectBank";
import React, { useLayoutEffect, useRef, useState } from "react";
import BottomSheet from "./BottomSheet";
import { PROFILE_ICONS } from "./profileMenuIcons";
import NotificationSettings from "./NotificationSettings";
import { FieldStatus } from "./FieldStatus";
import FaceIdSettingRow from "./FaceIdSettingRow";
import { FaChevronRight } from "react-icons/fa";
import { NATIVE_PUSH_SUPPORTED } from "../lib/nativePush";
import type { User } from "../types/User";
import type { ProfileSettings } from "../hooks/useProfileSettings";
import type { ProfileAutosave } from "../hooks/useProfileAutosave";
import "./ProfileSettingsSheet.css";
import { t } from "../i18n";
import { T } from "../i18n/T";
import { useFitText } from "../hooks/useFitText";

// The Settings drawer: Notifications (Desktop Companion / push) and the
// preference switches (Display, FastPass, Messages, Live/Offline control, the
// two receipt emails). Split out of pages/ProfilePage.tsx; the state lives in
// hooks/useProfileSettings.

// The Mac desktop app loads this same site inside Electron — its UA is the
// one reliable marker. The notifications "Desktop Companion" block shows
// ONLY there (founder decision): on plain web / mobile web it was pure noise
// now that the Mac app IS the desktop-notification path.
const IS_DESKTOP_APP = /Electron/i.test(navigator.userAgent);

/** The rows' padding on a short phone, and the least a row may keep when the sheet must fit one screen. */
const ROWS_PAD = 7;
const ROWS_PAD_MIN = 3;

// One Brand Green line icon per row (build 38 — the emojis are gone: no Apple
// emojis in product UI). 22px, currentColor.
const ic = (children: React.ReactNode) => (
  <span className="profile-setting-ic" aria-hidden="true">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  </span>
);
// The three request-card sizes, smallest first, with their names (the "A"s'
// spoken labels).
const CARD_SIZES = [
  ["compact", "profileSettingsSheet.compact"],
  ["standard", "profileSettingsSheet.standard"],
  ["large", "profileSettingsSheet.large"],
] as const;

// The Emails sheet's title icon (an envelope, like its row).
const MAIL_TITLE_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></svg>
);

const SETTING_ICONS = {
  laptop: ic(<><rect x="4" y="5" width="16" height="11" rx="1.5" /><path d="M2.5 19h19" /></>),
  display: ic(<><path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" /><circle cx="12" cy="12" r="2.6" /></>),
  fastPass: ic(<path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12L13 2Z" />),
  messages: ic(<><path d="M4 5h16v11H9l-5 4V5Z" /><path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" /></>),
  // A toggle switch — the control itself, not a broadcast wave (founder, round 4).
  live: ic(<><rect x="2.5" y="7" width="19" height="10" rx="5" /><circle cx="16.5" cy="12" r="2.4" fill="currentColor" stroke="none" /></>),
  mail: ic(<><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></>),
  bank: ic(<><path d="M3 10 12 4l9 6" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" /><path d="M3 20h18" /></>),
  // Headphones (founder, round 4).
  help: ic(<><path d="M4 14v-2a8 8 0 0 1 16 0v2" /><rect x="2.5" y="13.5" width="4.5" height="6.5" rx="2.2" /><rect x="17" y="13.5" width="4.5" height="6.5" rx="2.2" /></>),
  // A face in a scan frame.
  faceId: ic(<><path d="M3.5 8V6a2.5 2.5 0 0 1 2.5-2.5h2M16 3.5h2A2.5 2.5 0 0 1 20.5 6v2M20.5 16v2a2.5 2.5 0 0 1-2.5 2.5h-2M8 20.5H6A2.5 2.5 0 0 1 3.5 18v-2" /><path d="M9 9.5v1.5M15 9.5v1.5M12 9.5v4h-1M9.5 16a3.5 3.5 0 0 0 5 0" /></>),
};

// Face ID: the App-level one-time prompt asks once; after that, the row in
// General (FaceIdSettingRow) is the way to turn it on — and it only exists
// while it is off (founder: once they add it, it stays).

type Props = {
  open: boolean;
  onClose: () => void;
  profile: User;
  user: User | null;
  settings: ProfileSettings;
  autosave: ProfileAutosave;
  /** "Help, terms & support" → closes this sheet and opens the chooser (owned by the More menu). */
  onNeedHelp: () => void;
};

/* The Requests size title beside its three "A"s: one line, shrinking in a wider
   font than iPhone's instead of ending in "…". */
const RequestsSizeLabel: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const label = t("profileSettingsSheet.requestsSize");
  useFitText(ref, label, 11);
  return (
    <div ref={ref} className="profile-setting-label">
      {label}
    </div>
  );
};

export default function ProfileSettingsSheet({ open, onClose, profile, user, settings, autosave, onNeedHelp }: Props) {
  const {
    isWebPushEnabled,
    setIsWebPushEnabled,
    showFastPass,
    setShowFastPass,
    allowPersonalizedMessages,
    setAllowPersonalizedMessages,
    showLiveToggle,
    emailPayoutStarted,
    setEmailPayoutStarted,
    emailDepositLanded,
    setEmailDepositLanded,
    requestCardSize,
    desktopActive,
    setDesktopActive,
    toggleLiveControl,
    pickCardSize,
  } = settings;
  const { fieldStatus, toggleField } = autosave;
  // The Emails sheet, on top of this one (1.5.2 b48).
  const [emailsOpen, setEmailsOpen] = useState(false);
  // One screen, no scroll (founder). Where every row doesn't fit — the app with
  // Notifications + Face ID off on a notched 375 × 812, a wider font, a longer
  // language — each row gives up just the padding the sheet needs (never below
  // 3 px), re-measured until it fits. Rows arrive late (Face ID after its
  // check), so the sheet is measured again whenever its rows change. Where it
  // fits, nothing changes. (Spotlight moved to the QR Code tab, 2026-10-19.)
  const firstSection = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const body = firstSection.current?.closest<HTMLElement>(".bsheet-body");
    if (!body) return;
    const fit = () => {
      for (let step = 0; step < 6; step++) {
        const over = body.scrollHeight - body.clientHeight;
        const pad = body.dataset.rowsPad ? Number(body.dataset.rowsPad) : ROWS_PAD;
        if (over <= 1 || (body.dataset.rowsPad && pad <= ROWS_PAD_MIN)) return;
        const rows = Math.max(1, body.querySelectorAll(".profile-settings .profile-setting-row, .profile-settings .profile-notif-option").length);
        const next = Math.max(ROWS_PAD_MIN, pad - Math.ceil(over / (2 * rows)));
        body.dataset.rowsPad = String(next);
        body.style.setProperty("--rows-pad", `${next}px`);
      }
    };
    fit();
    const watch = new MutationObserver(fit);
    watch.observe(body, { childList: true, subtree: true });
    return () => watch.disconnect();
  }, [open]);
  return (
    <>
      {/* ── Notifications drawer — Desktop Companion / push (existing
          section, moved into the drawer; its title is now the drawer title). ── */}
      <BottomSheet open={open} title={t("profileSettingsSheet.settings")} icon={PROFILE_ICONS.settings} onClose={onClose} roomy>
      {/* ── Notifications half (founder: merged Settings sheet).
          The whole section hides when it has nothing to show — a DJ on
          plain web has no push block (native/Mac-app only now), and the
          email receipts live in the settings card below: an empty labeled
          card would be worse than none. ── */}
      <div className="profile-sheet-sec" ref={firstSection}>{t("profileSettingsSheet.general")}</div>
      <div className="profile-settings profile-group-card">
      {(NATIVE_PUSH_SUPPORTED || IS_DESKTOP_APP) && (
      <div className="profile-notifications">
        {/* Push Notifications (native app) / Desktop Companion
            (Mac app ONLY, founder 8/29). Plain web + mobile web render
            NOTHING here — the old "open a supported browser to install"
            upsell ate space for no reason now that the Mac app is the
            desktop path. Existing browser-push subscribers keep receiving;
            they've just lost a settings surface they no longer need. */}
        <div className="profile-notif-option">
          {!NATIVE_PUSH_SUPPORTED && (
            <div className="profile-notif-option-head">
              <div className="profile-notif-option-text">
                <div className="profile-notif-option-label">
                  {SETTING_ICONS.laptop}
                  <T k="profileSettingsSheet.desktopCompanionRecommended" tags={{ 0: (c) => <span style={{ color: "#a78bfa", fontWeight: 600, fontSize: "0.82em", marginLeft: 6 }}>{c}</span> }} />
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={desktopActive}
                aria-label={t("profileSettingsSheet.desktopNotificationsStatus")}
                title={desktopActive
                  ? t("profileSettingsSheet.youLlReceiveDesktop")
                  : t("profileSettingsSheet.openWwwRequestaApp")}
                disabled
                className={`profile-toggle ${desktopActive ? "on" : ""}`}
              >
                <span className="profile-toggle-knob" />
              </button>
            </div>
          )}
          <NotificationSettings
            userSlug={user!.slug}
            initialWebPushEnabled={isWebPushEnabled}
            onChange={setIsWebPushEnabled}
            onActiveChange={setDesktopActive}
          />
        </div>
      </div>
      )}
      {/* One scale for every row (build 38, founder mockup): section label → card →
          [green line icon · title · one grey line · control]. Every switch,
          its wording and its autosave are unchanged. Notifications + Display
          share ONE "General" card (founder, round 4). */}
        {/* Requests-tab card size (3 sizes). Accounts from before the feature
            read null → Compact; new accounts start on Standard (server hook). */}
        {/* "Requests size" (founder pick 3A, 1.5.2: it only sizes the request
            cards): ONE row — the title, then three "A"s drawn at the three
            sizes. The old Compact / Standard / Large words dropped to a second
            line on iPhone; the letters never do. */}
        <div className="profile-setting-row profile-setting-row--size">
          {SETTING_ICONS.display}
          <div className="profile-setting-text">
            <RequestsSizeLabel />
          </div>
          <div className="profile-display-toggle profile-size-aaa" role="radiogroup" aria-label={t("profileSettingsSheet.requestCardSize")}>
            {CARD_SIZES.map(([v, name]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={requestCardSize === v}
                aria-label={t(name)}
                title={t(name)}
                className={`profile-display-btn profile-size-aaa-btn--${v}${requestCardSize === v ? " profile-display-btn--active" : ""}`}
                onClick={() => pickCardSize(v)}
              >
                A
              </button>
            ))}
          </div>
        </div>
        <FieldStatus status={fieldStatus.requestCardSize} />
        {/* Native app only, and only while Face ID is off on this phone (founder, 1.5.1 #5). */}
        <FaceIdSettingRow user={user} icon={SETTING_ICONS.faceId} />
      </div>

      <div className="profile-sheet-sec profile-sheet-sec--gap">{t("profileSettingsSheet.requestFeatures")}</div>
      <div className="profile-settings profile-group-card">
        <div className="profile-setting-row">
          {SETTING_ICONS.fastPass}
          <div className="profile-setting-text">
            <div className="profile-setting-label"><T k="profileSettingsSheet.fastpass" tags={{ 0: (c) => <span className="profile-setting-chip">{c}</span> }} /></div>
            <div className="profile-setting-sub">{t("profileSettingsSheet.letFansPayTo")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showFastPass}
            aria-label={t("profileSettingsSheet.showFastPass")}
            className={`profile-toggle ${showFastPass ? "on" : ""}`}
            onClick={() => (takesMoney(profile) ? toggleField("fastPass", "showFastPass", showFastPass, setShowFastPass) : openMoneyGate(profile))}
          >
            <span className="profile-toggle-knob" />
          </button>
        </div>
        <FieldStatus status={fieldStatus.fastPass} />
        <div className="profile-setting-row">
          {SETTING_ICONS.messages}
          <div className="profile-setting-text">
            <div className="profile-setting-label">{t("profileSettingsSheet.messages")}</div>
            <div className="profile-setting-sub">{t("profileSettingsSheet.letFansAddA")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={allowPersonalizedMessages}
            aria-label={t("profileSettingsSheet.allowMessages")}
            className={`profile-toggle ${allowPersonalizedMessages ? "on" : ""}`}
            onClick={() => toggleField("messages", "allowPersonalizedMessages", allowPersonalizedMessages, setAllowPersonalizedMessages)}
          >
            <span className="profile-toggle-knob" />
          </button>
        </div>
        <FieldStatus status={fieldStatus.messages} />
        {/* ON shows the Live/Offline pill in the green header so the talent can
            go Offline; OFF hides it and keeps them Live. */}
        <div className="profile-setting-row">
          {SETTING_ICONS.live}
          <div className="profile-setting-text">
            <div className="profile-setting-label">{t("profileSettingsSheet.liveOfflineControl")}</div>
            <div className="profile-setting-sub">{t("profileSettingsSheet.soYouCanPause")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showLiveToggle}
            aria-label={t("profileSettingsSheet.showLiveOfflineControl")}
            className={`profile-toggle ${showLiveToggle ? "on" : ""}`}
            onClick={toggleLiveControl}
          >
            <span className="profile-toggle-knob" />
          </button>
        </div>
        <FieldStatus status={fieldStatus.liveToggle} />
      </div>

      {/* Opens the "Help, terms & support" chooser (owned by the More menu):
          Help Center (= Share Feedback) and Terms (Terms, Privacy, Delete account). */}
      {/* Emails is one row now (founder, 1.5.2 b48: the two switches took
          substantial room at the bottom) — it opens its own small sheet, on
          top of this one, with the same two switches. One card with Help. */}
      <div className="profile-settings profile-group-card profile-setting-help">
        <button type="button" className="profile-setting-row profile-setting-row--button profile-setting-row--emails" onClick={() => setEmailsOpen(true)}>
          {SETTING_ICONS.mail}
          <span className="profile-setting-text">
            <span className="profile-setting-label">{t("profileSettingsSheet.emails")}</span>
          </span>
          <FaChevronRight className="profile-menu-chev" aria-hidden />
        </button>
        <button type="button" className="profile-setting-row profile-setting-row--button" onClick={onNeedHelp}>
          {SETTING_ICONS.help}
          <span className="profile-setting-text">
            <span className="profile-setting-label">{t("profileSettingsSheet.helpTermsSupport")}</span>
          </span>
          <FaChevronRight className="profile-menu-chev" aria-hidden />
        </button>
      </div>
      {/* --- /Settings Section --- */}
      </BottomSheet>
      {/* The two receipt emails. The safety emails (payout failed / Stripe
          needs info) have no switch — always sent. */}
      <BottomSheet open={open && emailsOpen} title={t("profileSettingsSheet.emails")} icon={MAIL_TITLE_ICON} onClose={() => setEmailsOpen(false)} className="emails-bsheet">
      <div className="profile-settings profile-group-card">
        <div className="profile-setting-row">
          {SETTING_ICONS.mail}
          <div className="profile-setting-text">
            <div className="profile-setting-label">{t("profileSettingsSheet.withdrawalEmails")}</div>
            <div className="profile-setting-sub">{t("profileSettingsSheet.anEmailWhenA")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={emailPayoutStarted}
            aria-label={t("profileSettingsSheet.withdrawalEmails2")}
            className={`profile-toggle ${emailPayoutStarted ? "on" : ""}`}
            onClick={() =>
              toggleField("emailPayoutStarted", "emailPayoutStarted", emailPayoutStarted, setEmailPayoutStarted)
            }
          >
            <span className="profile-toggle-knob" />
          </button>
        </div>
        <FieldStatus status={fieldStatus.emailPayoutStarted} />
        <div className="profile-setting-row">
          {SETTING_ICONS.bank}
          <div className="profile-setting-text">
            <div className="profile-setting-label">{t("profileSettingsSheet.depositEmails")}</div>
            <div className="profile-setting-sub">{t("profileSettingsSheet.getAnEmailWhen")}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={emailDepositLanded}
            aria-label={t("profileSettingsSheet.depositEmails2")}
            className={`profile-toggle ${emailDepositLanded ? "on" : ""}`}
            onClick={() =>
              toggleField("emailDepositLanded", "emailDepositLanded", emailDepositLanded, setEmailDepositLanded)
            }
          >
            <span className="profile-toggle-knob" />
          </button>
        </div>
        <FieldStatus status={fieldStatus.emailDepositLanded} />
      </div>
      </BottomSheet>
    </>
  );
}
